/**
 * Standard inline notice/alert bar - the soft-bordered style used by the
 * feedback nudge on admin pages: a 1px tinted border + a faint tinted fill +
 * rounded corners + a leading severity icon + an optional right-aligned action.
 * No thick coloured edge.
 *
 * Severity-aware and theme-driven: the border, fill and icon colours all derive
 * from `theme.palette[severity].main`, so the palette lives in one place. Use
 * this instead of hand-rolling a bordered `<Box>` or a bare MUI `<Alert>` when
 * you need a persistent in-page notice (errors, warnings, info, success).
 */
import React from 'react'
import { Box, Typography, alpha } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

export type NoticeSeverity = 'error' | 'warning' | 'info' | 'success'

const DEFAULT_ICON: Record<NoticeSeverity, React.ElementType> = {
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
  success: CheckCircleOutlineIcon,
}

export interface NoticeBarProps {
  severity?: NoticeSeverity
  /** Bold lead line. */
  title?: React.ReactNode
  /** Body content under the title (or the whole message when no title). */
  children?: React.ReactNode
  /** Right-aligned action (e.g. a Button/Link). */
  action?: React.ReactNode
  /** Override the default severity icon, or pass `false` to hide it. */
  icon?: React.ReactNode | false
  sx?: SxProps<Theme>
  role?: string
}

const NoticeBar: React.FC<NoticeBarProps> = ({
  severity = 'info',
  title,
  children,
  action,
  icon,
  sx,
  role = 'status',
}) => {
  const Icon = DEFAULT_ICON[severity]
  const multiLine = Boolean(title && children)

  return (
    <Box
      role={role}
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: alpha(theme.palette[severity].main, 0.3),
          bgcolor: alpha(theme.palette[severity].main, 0.08),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon === false ? null : (
        <Box sx={{ display: 'flex', color: `${severity}.main`, mt: multiLine ? '-1px' : 0 }}>
          {icon ?? <Icon fontSize="small" />}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
        ) : null}
        {children ? (
          <Box sx={{ color: 'text.primary', fontSize: '0.875rem' }}>{children}</Box>
        ) : null}
      </Box>
      {action ? <Box sx={{ flexShrink: 0, ml: 0.5 }}>{action}</Box> : null}
    </Box>
  )
}

export default NoticeBar
